/**
 * НАЗНАЧЕНИЕ: Проверка выбранных файлов на соответствие формату SVS
 * ОПИСАНИЕ: Двухуровневая валидация до отправки файла на сервер:
 * - проверка расширения (.svs)
 * - проверка содержимого по сигнатуре TIFF и описанию Aperio (тег 270),
 *   что отсекает файлы, которым расширение .svs присвоено вручную
 * ОСОБЕННОСТИ: читаются только несколько десятков байт заголовка через
 * File.slice(), файл целиком в память не загружается
 * СЕРВЕР: аналогичная проверка продублирована в
 * backend/apps/slide/lib/svs_validation.py
 */
import { FILE_EXTENSION_ERROR_MESSAGE, SVS_EXTENSION } from "../constants";

const CLASSIC_TIFF_VERSION = 42;
const BIG_TIFF_VERSION = 43;

const IMAGE_DESCRIPTION_TAG = 270;
const ASCII_FIELD_TYPE = 2;

// Маркеры производителя в ImageDescription. SVS формируется сканерами Aperio
const SVS_DESCRIPTION_MARKERS = ["aperio"];

const MAX_DESCRIPTION_LENGTH = 4096;
const MAX_IFD_ENTRIES = 512;

const hasSvsExtension = (fileName) =>
    typeof fileName === "string" &&
    fileName.toLowerCase().endsWith(SVS_EXTENSION);

const blobToArrayBuffer = (blob) => {
    if (typeof blob.arrayBuffer === "function") {
        return blob.arrayBuffer();
    }

    // Запасной путь для браузеров без Blob.arrayBuffer()
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
    });
};

/**
 * Читает ровно size байт с позиции offset. Возвращает DataView или null.
 */
const readChunk = async (file, offset, size) => {
    if (offset < 0 || size <= 0 || offset + size > file.size) {
        return null;
    }

    const buffer = await blobToArrayBuffer(file.slice(offset, offset + size));

    if (!buffer || buffer.byteLength !== size) {
        return null;
    }

    return new DataView(buffer);
};

/**
 * Читает 64-битное смещение BigTIFF двумя 32-битными словами,
 * чтобы не зависеть от поддержки BigInt.
 * Возвращает null, если значение не помещается в безопасное целое.
 */
const readUint64 = (view, offset, littleEndian) => {
    const low = view.getUint32(offset + (littleEndian ? 0 : 4), littleEndian);
    const high = view.getUint32(offset + (littleEndian ? 4 : 0), littleEndian);
    const value = high * 4294967296 + low;

    return Number.isSafeInteger(value) ? value : null;
};

/**
 * Разбирает заголовок TIFF.
 * Возвращает { littleEndian, ifdOffset, isBigTiff } или null, если это не TIFF.
 */
const readTiffHeader = async (file) => {
    const header = await readChunk(file, 0, 16);

    if (!header) {
        return null;
    }

    const firstByte = header.getUint8(0);
    const secondByte = header.getUint8(1);

    // 'II' - little-endian, 'MM' - big-endian
    let littleEndian;
    if (firstByte === 0x49 && secondByte === 0x49) {
        littleEndian = true;
    } else if (firstByte === 0x4d && secondByte === 0x4d) {
        littleEndian = false;
    } else {
        return null;
    }

    const version = header.getUint16(2, littleEndian);

    if (version === CLASSIC_TIFF_VERSION) {
        return {
            littleEndian,
            ifdOffset: header.getUint32(4, littleEndian),
            isBigTiff: false,
        };
    }

    if (version === BIG_TIFF_VERSION) {
        // BigTIFF допускает только 8-байтовые смещения
        if (
            header.getUint16(4, littleEndian) !== 8 ||
            header.getUint16(6, littleEndian) !== 0
        ) {
            return null;
        }

        const ifdOffset = readUint64(header, 8, littleEndian);

        if (ifdOffset === null) {
            return null;
        }

        return { littleEndian, ifdOffset, isBigTiff: true };
    }

    return null;
};

/**
 * Ищет в первом IFD тег ImageDescription и возвращает его текст.
 * Возвращает null, если тег отсутствует или структура не читается.
 */
const readImageDescription = async (file, header) => {
    const { littleEndian, ifdOffset, isBigTiff } = header;

    if (!ifdOffset) {
        return null;
    }

    // В классическом TIFF количество записей IFD занимает 2 байта, а счетчик
    // значений внутри записи - 4 байта; в BigTIFF оба поля восьмибайтовые
    const ifdCountSize = isBigTiff ? 8 : 2;
    const valueCountSize = isBigTiff ? 8 : 4;
    const entrySize = isBigTiff ? 20 : 12;
    const valueSize = isBigTiff ? 8 : 4;

    const countView = await readChunk(file, ifdOffset, ifdCountSize);

    if (!countView) {
        return null;
    }

    const entriesCount = isBigTiff
        ? readUint64(countView, 0, littleEndian)
        : countView.getUint16(0, littleEndian);

    if (!entriesCount || entriesCount > MAX_IFD_ENTRIES) {
        return null;
    }

    const entries = await readChunk(
        file,
        ifdOffset + ifdCountSize,
        entriesCount * entrySize
    );

    if (!entries) {
        return null;
    }

    for (let index = 0; index < entriesCount; index += 1) {
        const entryOffset = index * entrySize;
        const tag = entries.getUint16(entryOffset, littleEndian);
        const fieldType = entries.getUint16(entryOffset + 2, littleEndian);

        if (tag !== IMAGE_DESCRIPTION_TAG || fieldType !== ASCII_FIELD_TYPE) {
            continue;
        }

        const rawLength = isBigTiff
            ? readUint64(entries, entryOffset + 4, littleEndian)
            : entries.getUint32(entryOffset + 4, littleEndian);

        if (!rawLength) {
            return null;
        }

        const length = Math.min(rawLength, MAX_DESCRIPTION_LENGTH);
        const valueOffset = entryOffset + 4 + valueCountSize;

        // Короткие значения хранятся прямо в записи IFD, длинные - по смещению
        let descriptionView;
        if (length <= valueSize) {
            descriptionView = new DataView(
                entries.buffer,
                entries.byteOffset + valueOffset,
                length
            );
        } else {
            const dataOffset = isBigTiff
                ? readUint64(entries, valueOffset, littleEndian)
                : entries.getUint32(valueOffset, littleEndian);

            if (dataOffset === null) {
                return null;
            }

            descriptionView = await readChunk(file, dataOffset, length);
        }

        if (!descriptionView) {
            return null;
        }

        let description = "";
        for (let byte = 0; byte < descriptionView.byteLength; byte += 1) {
            const code = descriptionView.getUint8(byte);

            if (code === 0) {
                break;
            }

            description += String.fromCharCode(code);
        }

        return description;
    }

    return null;
};

/**
 * Проверяет содержимое файла: сигнатуру TIFF и описание Aperio.
 */
const isSvsContent = async (file) => {
    const header = await readTiffHeader(file);

    if (!header) {
        return false;
    }

    const description = await readImageDescription(file, header);

    // Описание может отсутствовать у нестандартных, но валидных TIFF -
    // в этом случае доверяем сигнатуре формата
    if (!description) {
        return true;
    }

    const lowerCaseDescription = description.toLowerCase();

    return SVS_DESCRIPTION_MARKERS.some((marker) =>
        lowerCaseDescription.includes(marker)
    );
};

/**
 * Проверяет файл перед загрузкой.
 * Возвращает текст ошибки или null, если файл корректен.
 */
const validateSvsFile = async (file) => {
    if (!file || !file.name) {
        return FILE_EXTENSION_ERROR_MESSAGE;
    }

    if (!hasSvsExtension(file.name)) {
        return FILE_EXTENSION_ERROR_MESSAGE;
    }

    try {
        const isValidContent = await isSvsContent(file);

        return isValidContent ? null : FILE_EXTENSION_ERROR_MESSAGE;
    } catch (error) {
        // Если заголовок прочитать не удалось, решение остается за сервером:
        // там выполняется такая же проверка
        console.warn(`Не удалось проверить заголовок файла ${file.name}:`, error);

        return null;
    }
};

export { hasSvsExtension, isSvsContent, validateSvsFile };
